import type { RecordingConsentState } from '@/custolink-views/shared/recording_consent/useRecordingConsent'
import { cn } from '@/custolink-newdesign/lib/utils'
import { Button } from '@/custolink-newdesign/ui/button'

/**
 * RecordingConsentCard - the new-design counterpart of RecordingConsentBanner, used by the
 * worker room (RemoteSupportRoom) and the guest technician room (MobileTechnicianRoom).
 *
 * It renders only what the server already validated: `consent.ui` is the single mapping from the
 * server's recording status to a message and the actions this participant may take, so the card
 * never decides on its own that capture is on (D-09, T-06.1-49). Every string is a React text
 * child, so server-provided text can never become markup (T-06.1-50).
 *
 * A participant whose token withholds publishing (D-06) is told why their camera and microphone
 * are not on yet. This component does not turn them on: useRecordingConsent does that once the
 * server grants publish, because the SFU, not this card, owns the real rule.
 *
 * Placement: absolute at the bottom-left of the stage, just above the call bar on desktop and
 * full-width above it on mobile. The caller supplies a relatively positioned stage container.
 */

type CardLayout = 'desktop' | 'mobile'

const CARD_POSITION: Record<CardLayout, string> = {
  desktop: 'cl:bottom-3 cl:left-3 cl:max-w-sm',
  mobile: 'cl:bottom-3 cl:left-3 cl:right-3',
}

const PILL_POSITION: Record<CardLayout, string> = {
  desktop: 'cl:bottom-3 cl:left-3',
  mobile: 'cl:bottom-3 cl:left-3 cl:right-3',
}

export function RecordingConsentCard(p: {
  consent: RecordingConsentState
  layout: CardLayout
  /** Mobile: the header now carries the stop action, so no floating recording pill is drawn. */
  suppressRecordingPill?: boolean
  /** Mobile: raise the decision card above the stage's bottom drawing toolbar so they never stack. */
  lifted?: boolean
}) {
  const { consent, layout } = p
  const { ui, deciding, error, publishHeld } = consent

  if (ui.kind === 'hidden' || ui.kind === 'saved') {
    return null
  }

  // 44 px controls on the phone; the same buttons stay compact on the desktop stage.
  const touchTarget = layout === 'mobile' ? 'cl:min-h-[44px]' : undefined
  const buttonSize = layout === 'mobile' ? 'lg' : 'sm'
  // The only role-dependent copy. The worker room renders this card with layout="desktop" and the
  // guest technician room with layout="mobile", so the layout selects the wording: the expert
  // records the call, the customer agrees to it.
  const acceptLabel = layout === 'desktop' ? 'Record' : 'I agree'

  // Capture is confirmed (starting/recording/stopping): a compact pill, not the full card.
  // On mobile the caller suppresses this pill and docks the same message/stop into the header.
  if (ui.kind === 'recording' || ui.kind === 'stopping') {
    if (p.suppressRecordingPill) {
      return null
    }
    return (
      <div
        role="region"
        aria-label="Recording"
        aria-live="polite"
        className={cn(
          'cl:absolute cl:z-30 cl:flex cl:max-w-[calc(100%-1.5rem)] cl:items-center cl:gap-2 cl:rounded-full cl:bg-neutral-950/90 cl:px-3 cl:py-1.5 cl:text-xs cl:text-white cl:shadow-lg',
          PILL_POSITION[layout],
        )}
      >
        <span
          className="cl:size-2 cl:shrink-0 cl:rounded-full cl:bg-red-500"
          aria-hidden="true"
        />
        <span className="cl:min-w-0 cl:truncate">{ui.message}</span>
        {ui.canDecline && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={deciding}
            className={cn('cl:shrink-0', touchTarget)}
            onClick={() => consent.decide(false)}
          >
            Stop recording
          </Button>
        )}
        {error != null && (
          <span className="cl:shrink-0 cl:text-red-300" role="alert">
            {error}
          </span>
        )}
      </div>
    )
  }

  const primaryLabel =
    ui.kind === 'ask' ? acceptLabel : ui.kind === 'declined-by-me' ? 'Allow recording' : null
  const secondaryLabel = ui.kind === 'ask' ? "Don't record" : ui.canDecline ? 'Stop recording' : null

  // When the mobile drawing toolbar owns the bottom of the stage, sit above it (bottom-16 =
  // 64px clears the toolbar's 8px inset + ~52px height) instead of covering it.
  const cardPosition =
    layout === 'mobile' && p.lifted === true
      ? 'cl:bottom-16 cl:left-3 cl:right-3'
      : CARD_POSITION[layout]

  return (
    <div
      role="region"
      aria-label="Recording"
      aria-live="polite"
      className={cn(
        'cl:absolute cl:z-30 cl:flex cl:flex-col cl:gap-2 cl:rounded-xl cl:bg-neutral-950/85 cl:p-3 cl:text-sm cl:text-white cl:shadow-lg',
        cardPosition,
      )}
    >
      <p className="cl:flex cl:items-center cl:gap-2">
        {ui.capturing && (
          <span
            className="cl:size-2 cl:shrink-0 cl:rounded-full cl:bg-red-500"
            aria-hidden="true"
          />
        )}
        <span>{ui.message}</span>
      </p>

      {publishHeld && (
        <p className="cl:text-xs cl:text-white/80">
          Your camera and microphone turn on after you answer.
        </p>
      )}

      {error != null && (
        <p className="cl:text-xs cl:text-red-300" role="alert">
          {error}
        </p>
      )}

      {(primaryLabel != null || secondaryLabel != null) && (
        <div className="cl:flex cl:flex-wrap cl:gap-2">
          {primaryLabel != null && (
            <Button
              type="button"
              variant="default"
              size={buttonSize}
              disabled={deciding}
              className={cn(touchTarget)}
              onClick={() => consent.decide(true)}
            >
              {primaryLabel}
            </Button>
          )}
          {secondaryLabel != null && (
            <Button
              type="button"
              variant="secondary"
              size={buttonSize}
              disabled={deciding}
              className={cn(touchTarget)}
              onClick={() => consent.decide(false)}
            >
              {secondaryLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default RecordingConsentCard
